package one.healthium.uscreen.ui.blocks

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import one.healthium.ui.compose.modifier.onClickHaptic
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.uscreen.domain.models.ButtonBlock
import one.healthium.uscreen.domain.models.ButtonStyle
import one.healthium.uscreen.domain.models.CtaAction
import one.healthium.uscreen.ui.components.pressScale
import one.healthium.uscreen.ui.util.clickableHapticOnPress
import one.healthium.uscreen.ui.util.resolveNamedColor
import one.healthium.uscreen.ui.util.uscreenPadding

private val DEFAULT_BUTTON_HEIGHT = 60.dp
private val BUTTON_SHAPE = RoundedCornerShape(50)

@Composable
fun UScreenButtonBlock(
    modifier: Modifier = Modifier,
    block: ButtonBlock,
    onAction: () -> Unit,
) {
    val tag = when (block.action) {
        CtaAction.SignIn -> "sign_in_button"
        is CtaAction.OpenUrl -> "uscreen_button_url"
        is CtaAction.GoToScreen -> "uscreen_button_screen"
        else -> "uscreen_button"
    }
    val tagModifier = Modifier.testTag(tag)
    val hapticAction = onClickHaptic(onClick = onAction)

    val resolvedColor = block.color?.let { resolveNamedColor(it) }
    val resolvedTextColor = block.textColor?.let { resolveNamedColor(it) }
    val widthModifier = if (block.fullWidth) Modifier.fillMaxWidth() else Modifier
    val heightModifier = Modifier.height(block.heightPx?.dp ?: DEFAULT_BUTTON_HEIGHT)

    Box(
        modifier = modifier.fillMaxWidth(),
        contentAlignment = Alignment.Center,
    ) {
        when (block.style) {
            ButtonStyle.Primary -> {
                Surface(
                    modifier = widthModifier
                        .then(heightModifier)
                        .uscreenPadding(block.padding)
                        .pressScale()
                        .then(tagModifier),
                    shape = BUTTON_SHAPE,
                    color = resolvedColor ?: AppTheme.colors.primary,
                ) {
                    Box(
                        modifier = Modifier.clickableHapticOnPress(onClick = onAction),
                        contentAlignment = Alignment.Center,
                    ) {
                        Text(
                            text = block.text,
                            style = AppTheme.textStyles.button,
                            color = resolvedTextColor ?: AppTheme.uscreenStyle.ctaContentColor,
                        )
                    }
                }
            }
            ButtonStyle.Text -> {
                TextButton(
                    modifier = widthModifier
                        .uscreenPadding(block.padding)
                        .then(tagModifier),
                    onClick = hapticAction,
                ) {
                    Text(
                        text = block.text,
                        style = AppTheme.textStyles.body1Medium,
                        color = resolvedTextColor ?: AppTheme.colors.primary,
                    )
                }
            }
            ButtonStyle.Outline -> {
                val outlineColor = resolvedColor ?: AppTheme.colors.primary
                OutlinedButton(
                    modifier = widthModifier
                        .then(heightModifier)
                        .uscreenPadding(block.padding)
                        .then(tagModifier),
                    onClick = hapticAction,
                    shape = BUTTON_SHAPE,
                    border = BorderStroke(1.5.dp, outlineColor),
                    colors = ButtonDefaults.outlinedButtonColors(
                        contentColor = resolvedTextColor ?: outlineColor,
                    ),
                ) {
                    Text(
                        text = block.text,
                        style = AppTheme.textStyles.body1Medium,
                    )
                }
            }
        }
    }
}
